// src/components/BookingForm.jsx

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Skeleton,
  useTheme,
  useMediaQuery,
  Chip,
  Grid,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { todayInOrgTimezone } from "../utils/timezoneUtils";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';

import {
  createBooking,
  getFreeSlots,
  getBookingsByRoom,
} from "../services/bookingService";
import { getCurrentUser } from "../services/authService";
import { useTimezone } from "../contexts/TimezoneContext";

// Styled Components
const BookingContainer = styled(Paper)(({ theme }) => ({
  padding: 0,
  marginTop: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: 'none', // Remove shadow
}));

const BookingHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const SplitContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '500px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const CalendarSection = styled(Box)(({ theme }) => ({
  flex: '0 0 400px',
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down('md')]: {
    flex: '1 1 auto',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const TimeSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
}));

const TimeSlotButton = styled(Button)(({ theme, isSelected, isDisabled }) => ({
  minWidth: '80px',
  minHeight: '40px',
  margin: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 500,
  fontSize: '0.875rem',
  textTransform: 'none',
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isDisabled && {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[400],
    border: `1px solid ${theme.palette.grey[300]}`,
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  }),
  ...(!isSelected && !isDisabled && {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
  }),
}));

const BottomSummary = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

const SummaryIcon = styled(Box)(({ theme, hasValue }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: hasValue ? 'transparent' : theme.palette.grey[100],
  color: hasValue ? theme.palette.primary.main : theme.palette.grey[500],
  minWidth: '200px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  alignSelf: 'flex-start',
  textTransform: 'none',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
}));


function BookingForm({
  roomId,
  onBookingSuccess,
  onBookingError,
  selectedDate,
  onDateChange,
}) {
  const [userId, setUserId] = useState("");
  const { orgTimeZone } = useTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);

  // Organization settings
  const [orgStartTime, setOrgStartTime] = useState("08:00");
  const [orgEndTime, setOrgEndTime] = useState("18:00");

  const [availableSlots, setAvailableSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState({});
  const [startSlot, setStartSlot] = useState("");
  const [endSlot, setEndSlot] = useState("");
  const [bookings, setBookings] = useState([]);
  const [meetingName, setMeetingName] = useState("");

  // UI State
  const [showTimeSelection, setShowTimeSelection] = useState(false);
  const [showEndTimeSelection, setShowEndTimeSelection] = useState(false);
  const bottomSummaryRef = useRef(null);
  const timeSectionRef = useRef(null);

  useEffect(() => {
    if (user) {
      console.log('Loading user and organization settings in BookingForm:', user);
      setUserId(user._id);
      
      // Get organization allowed hours from Redux state
      if (user.currentOrganization) {
        console.log('Organization found:', user.currentOrganization);
        if (user.currentOrganization.defaultStartTime) {
          console.log('Setting booking form start time:', user.currentOrganization.defaultStartTime);
          setOrgStartTime(user.currentOrganization.defaultStartTime);
        }
        if (user.currentOrganization.defaultEndTime) {
          console.log('Setting booking form end time:', user.currentOrganization.defaultEndTime);
          setOrgEndTime(user.currentOrganization.defaultEndTime);
        }
      } else {
        console.log('No organization found for user');
      }
    } else {
      console.log('No user found in Redux state');
      onBookingError("Please login first");
    }
  }, [user, onBookingError]);

  // Update slots when organization hours change
  useEffect(() => {
    if (selectedDate && orgStartTime && orgEndTime) {
      // Regenerate slots with new organization hours
      const allPossibleSlots = generateAllSlots();
      const filteredSlots = filterPastTimes(allPossibleSlots, selectedDate);
      setAllSlots(filteredSlots);
    }
  }, [orgStartTime, orgEndTime, selectedDate, orgTimeZone]);

  // Generate time slots based on organization allowed hours
  function generateAllSlots() {
    const slots = [];
    
    // Ensure organization hours are loaded
    if (!orgStartTime || !orgEndTime) {
      return slots;
    }
    
    const [startHour, startMinute] = orgStartTime.split(':').map(Number);
    const [endHour, endMinute] = orgEndTime.split(':').map(Number);
    
    // Convert to total minutes for easier calculation
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    
    // Generate slots every 30 minutes
    for (let minutes = startTotalMinutes; minutes < endTotalMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      slots.push(timeStr);
    }
    
    return slots;
  }

  // Filter out past times for today
  function filterPastTimes(slots, selectedDate) {
    if (!selectedDate) return slots;
    
    const today = moment.tz(orgTimeZone);
    const selectedMoment = moment.tz(selectedDate, orgTimeZone).startOf('day');
    
    // If selected date is not today, return all slots
    if (!selectedMoment.isSame(today, 'day')) {
      return slots;
    }
    
    // Filter out past times for today
    return slots.filter(slot => {
      const [hour, minute] = slot.split(':').map(Number);
      const slotTime = selectedMoment.clone().set({ hour, minute });
      return slotTime.isAfter(today);
    });
  }

  // Date change handler
  async function handleDateChangeInternal(newDate) {
    if (newDate) {
      console.log('🗓️ Date selected:', newDate);
    }
    
    if (onDateChange) {
      onDateChange(newDate);
    }
    
    // Reset time selections
    setStartSlot("");
    setEndSlot("");
    setShowTimeSelection(!!newDate);
    setShowEndTimeSelection(false);

    if (!newDate) {
      setAvailableSlots([]);
      setBookedSlots({});
      setAllSlots([]);
      return;
    }

    const dateStr = formatDateYYYYMMDD(newDate);
    try {
      const freeSlots = await getFreeSlots(roomId, dateStr);
      setAvailableSlots(freeSlots);

      const allBookings = await getBookingsByRoom(roomId);
      setBookings(allBookings);

      // Generate all possible slots based on organization hours
      const allPossibleSlots = generateAllSlots();
      // Filter out past times if selected date is today
      const filteredSlots = filterPastTimes(allPossibleSlots, newDate);
      setAllSlots(filteredSlots);

      // Create mapping of booked slots to users
      const bookedSlotsMap = {};
      allBookings.forEach(booking => {
        const bookingDate = moment.tz(booking.startTime, orgTimeZone);
        const selectedMoment = moment.tz(newDate, orgTimeZone);
        
        if (bookingDate.isSame(selectedMoment, 'day')) {
          const startTime = bookingDate.format('HH:mm');
          const endTime = moment.tz(booking.endTime, orgTimeZone).format('HH:mm');
          
          // Mark all slots from start to end as booked
          let currentSlot = startTime;
          while (currentSlot < endTime) {
            bookedSlotsMap[currentSlot] = {
              user: booking.user?.username || 'Unknown User',
              userId: booking.user?._id,
              bookingId: booking._id,
            };
            currentSlot = nextHalfHour(currentSlot);
          }
        }
      });
      
      setBookedSlots(bookedSlotsMap);
    } catch (err) {
      onBookingError("Error fetching data");
      console.error(err);
    }
  }

  function formatDateYYYYMMDD(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateToDisplay(dateObj) {
    if (!dateObj) return "";
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[dateObj.getMonth()];
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    return `${month} ${day.toString().padStart(2, '0')}, ${year}`;
  }

  function nextHalfHour(slot) {
    const [hh, mm] = slot.split(":").map(Number);
    let newMm = mm + 30;
    let newHh = hh;
    if (newMm === 60) {
      newHh += 1;
      newMm = 0;
    }
    return `${String(newHh).padStart(2, "0")}:${String(newMm).padStart(2, "0")}`;
  }

  function handleStartSlotClick(slot) {
    if (bookedSlots[slot]) return; // Don't allow clicking on booked slots
    
    setStartSlot(slot);
    setEndSlot("");
    setShowEndTimeSelection(true);
  }

  function getEndSlotOptions() {
    if (!startSlot || !selectedDate) return [];
    
    const results = [];
    let currentSlot = startSlot;
    
    while (true) {
      currentSlot = nextHalfHour(currentSlot);
      if (!allSlots.includes(currentSlot)) break;
      if (bookedSlots[currentSlot]) break; // Stop at next booking
      results.push(currentSlot);
    }
    
    return results;
  }

  function handleEndSlotClick(slot) {
    setEndSlot(slot);
    // Don't auto-open confirmation modal - user can use the "Book Room" button
    if (isMobile) {
      // Collapse time options to reveal summary and confirm area
      setShowEndTimeSelection(false);
      setShowTimeSelection(false);
      // Smooth scroll to summary for easy confirmation
      setTimeout(() => {
        if (bottomSummaryRef.current && bottomSummaryRef.current.scrollIntoView) {
          bottomSummaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
      }, 0);
    }
  }

  function handleEditTimeClick() {
    setShowTimeSelection(true);
    setShowEndTimeSelection(!!startSlot);
    // Scroll back to the time section for editing
    setTimeout(() => {
      if (timeSectionRef.current && timeSectionRef.current.scrollIntoView) {
        timeSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }

  function handleBackToStartTime() {
    setEndSlot("");
    setShowEndTimeSelection(false);
  }

  async function handleConfirmBooking() {
    if (!selectedDate || !startSlot || !endSlot) {
      onBookingError("Please complete all booking details");
      return;
    }

    const startTime = combineDateAndTime(selectedDate, startSlot);
    const endTime = combineDateAndTime(selectedDate, endSlot);

    // Check if booking is in the past
    const now = moment.tz(orgTimeZone);
    const startMoment = moment.tz(startTime, orgTimeZone);
    
    if (startMoment.isBefore(now)) {
      onBookingError("Cannot book in the past");
      return;
    }

    // Check if booking is within organization's allowed hours
    const endMoment = moment.tz(endTime, orgTimeZone);
    const [orgStartHour, orgStartMinute] = orgStartTime.split(':').map(Number);
    const [orgEndHour, orgEndMinute] = orgEndTime.split(':').map(Number);
    
    const dayStart = startMoment.clone().set({ hour: orgStartHour, minute: orgStartMinute, second: 0 });
    const dayEnd = startMoment.clone().set({ hour: orgEndHour, minute: orgEndMinute, second: 0 });
    
    if (startMoment.isBefore(dayStart) || endMoment.isAfter(dayEnd)) {
      onBookingError(
        `Time must be within organization's allowed hours: ${orgStartTime} - ${orgEndTime} (${orgTimeZone})`
      );
      return;
    }

    try {
      await createBooking({ 
        roomId, 
        userId, 
        startTime, 
        endTime,
        meetingName: meetingName || 'Meeting'
      });
      
      onBookingSuccess("Booking created successfully");
      
      // Reset form
      setStartSlot("");
      setEndSlot("");
      setMeetingName("");
      setShowTimeSelection(false);
      setShowEndTimeSelection(false);
      
      // Refresh data
      if (selectedDate) {
        handleDateChangeInternal(selectedDate);
      }
    } catch (err) {
      console.error("Booking error:", err);
      onBookingError("Error creating booking");
    }
  }

  function combineDateAndTime(dateObj, hhmm) {
    const [hh, mm] = hhmm.split(":").map(Number);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    const day = dateObj.getDate();
    
    const combinedMoment = moment.tz({
      year,
      month,
      date: day,
      hour: hh,
      minute: mm,
      second: 0,
      millisecond: 0
    }, orgTimeZone);
    
    return combinedMoment.toDate();
  }

  function renderTimeSlots(slots, selectedValue, onClickSlot, isEndTime = false) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
        {slots.map((slot) => {
          const isSelected = slot === selectedValue;
          const isBooked = bookedSlots[slot];
          const isDisabled = isBooked && !isEndTime;
          
          const button = (
            <TimeSlotButton
              key={slot}
              isSelected={isSelected}
              isDisabled={isDisabled}
              onClick={() => !isDisabled && onClickSlot(slot)}
              disabled={isDisabled}
            >
              {slot}
            </TimeSlotButton>
          );

          if (isDisabled) {
            return (
              <Tooltip
                key={slot}
                title={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Booked by:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2">
                        {isBooked.user}
                      </Typography>
                    </Box>
                  </Box>
                }
                arrow
                placement="top"
              >
                <span>{button}</span>
              </Tooltip>
            );
          }

          return button;
        })}
      </Box>
    );
  }

  return (
    <BookingContainer elevation={0}>
      <BookingHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EventIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Book a Meeting
          </Typography>
        </Box>
        <Chip 
          label={`Times in ${orgTimeZone}`} 
          size="small" 
          variant="outlined" 
          color="primary"
        />
      </BookingHeader>

      <SplitContainer>
        {/* Calendar Section */}
        <CalendarSection>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Choose A Day From Calendar
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={selectedDate || null}
              onChange={handleDateChangeInternal}
              minDate={todayInOrgTimezone(orgTimeZone)}
              sx={{
                '& .MuiPickersDay-root': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
                '& .MuiPickersDay-root.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                },
              }}
            />
          </LocalizationProvider>
        </CalendarSection>

        {/* Time Selection Section */}
        <TimeSection ref={timeSectionRef}>
          {!showTimeSelection && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: 'text.secondary'
            }}>
              <Typography variant="body1">
                Select a date from the calendar to view available times
              </Typography>
            </Box>
          )}

          {showTimeSelection && !showEndTimeSelection && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Select Meeting Time
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Start Time
              </Typography>
              {!orgStartTime || !orgEndTime ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="rectangular" width="100%" height={48} />
                  <Skeleton variant="rectangular" width="100%" height={48} />
                  <Skeleton variant="rectangular" width="100%" height={48} />
                </Box>
              ) : allSlots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No available times for this date
                </Typography>
              ) : (
                renderTimeSlots(allSlots, startSlot, handleStartSlotClick)
              )}
            </Box>
          )}

          {showEndTimeSelection && (
            <Box>
              <NavigationButton
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToStartTime}
              >
                Start Time
              </NavigationButton>
              
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Select Meeting Time
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                End Time
              </Typography>
              {renderTimeSlots(getEndSlotOptions(), endSlot, handleEndSlotClick, true)}
              
              {getEndSlotOptions().length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No available end times. Please select a different start time.
                </Typography>
              )}
            </Box>
          )}
        </TimeSection>
      </SplitContainer>

      {/* Bottom Summary */}
      <BottomSummary ref={bottomSummaryRef}>
        <SummaryIcon hasValue={!!selectedDate}>
          <CalendarTodayIcon />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Chosen Date:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectedDate ? formatDateToDisplay(selectedDate) : 'Select date...'}
            </Typography>
          </Box>
        </SummaryIcon>

        <SummaryIcon hasValue={!!startSlot && !!endSlot}>
          <AccessTimeIcon />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Meeting Time:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {startSlot && endSlot 
                ? `${startSlot} - ${endSlot}` 
                : startSlot 
                  ? `${startSlot} - Select end time...`
                  : 'Select meeting time...'
              }
            </Typography>
            {isMobile && (startSlot || endSlot) && (
              <Button 
                size="small" 
                onClick={handleEditTimeClick}
                sx={{ mt: 0.5, textTransform: 'none', p: 0, minWidth: 0 }}
              >
                Edit
              </Button>
            )}
          </Box>
        </SummaryIcon>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          minWidth: '200px',
          [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
          },
        }}>
          <EventIcon sx={{ color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Meeting Name:
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter meeting name (optional)..."
              value={meetingName}
              onChange={(e) => setMeetingName(e.target.value)}
              size="small"
              variant="outlined"
              disabled={!selectedDate || !startSlot || !endSlot}
              sx={{
                mt: 0.5,
                '& .MuiOutlinedInput-root': {
                  height: '36px',
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                },
              }}
            />
          </Box>
        </Box>

        <Button
          variant="contained"
          size="large"
          onClick={handleConfirmBooking}
          disabled={!selectedDate || !startSlot || !endSlot || !userId}
          sx={{
            minWidth: '160px',
            height: '48px',
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '1rem',
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '&:disabled': {
              backgroundColor: 'grey.300',
              color: 'grey.500',
            },
            [theme.breakpoints.down('sm')]: {
              minWidth: '100%',
              mt: 1,
            },
          }}
        >
          Book Room
        </Button>
      </BottomSummary>

    </BookingContainer>
  );
}

export default BookingForm;